import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { isField } from './is-field';
import { useWorkspaceWidth } from './zone';

/**
 * A list beside a record (the two-portals design, §4.3 and §5) — the Team Portal study's variant
 * B, a queue rail beside a workspace, made the frame every list-and-record area is drawn in.
 *
 * **With no record open, the list is the page**: its title is the `h1`, and it is the page's one
 * `main`. **With a record open, the record is the page's one `main`**, and the list — when it is
 * shown — sits beside it in a 320 px pane that is a complementary landmark titled by an `h2`. That
 * is why the Team Portal's shell draws no `main` of its own: a spec reading "the page" reads the
 * record, and never finds the list's rows or links among the record's.
 *
 * **The pane is shown when the record was opened from its list** — the list's links leave its
 * search in the history entry's state — **or when the record's address carries the list's
 * query**; otherwise the record is full width, and "Show list" puts the list back. Esc closes the
 * record, back to the list under the search it was opened from — but never out of a field, and
 * never while a menu or a dialog is open, whose own Esc it is.
 *
 * **A record can hold itself open**, by drawing an element marked `SPLIT_HOLD` for as long as
 * closing it would destroy something: an inline field being edited or saved (`InlineField`), or a
 * work item's write under way. A marker in the DOM rather than a hold on this context, for
 * `overlayOpen`'s reason: the question is asked of the page at the moment of the key, and a
 * record that forgets to release a hold on unmount cannot leak one. It is kept for what cannot
 * be had back: a page's own note of what a write said goes with Esc as with any other navigation,
 * and the record it described can be read again.
 *
 * Below 1024 px there is no room for two panes: the pane is not drawn and there is nothing to show
 * it with, so a phone gets the record alone.
 */

interface ISplitContext {
  shown: boolean;
  toggle: () => void;
}

const SplitContext = React.createContext<Nullable<ISplitContext>>(null);

/** Drawn inside a record, this keeps Esc from closing it — spread it on the element that must
 *  not be lost (`{...SPLIT_HOLD}`). */
export const SPLIT_HOLD = { 'data-split-hold': '' } as const;

/** Inside a record drawn in a split view: whether its list is shown, and the switch for it. */
export function useSplit(): Nullable<ISplitContext> {
  return React.useContext(SplitContext);
}

export function SplitView({
  record,
  listInitially,
  paneTitle,
  pane,
  closeTo,
  children
}: {
  /** Whether a record is open — the route below names one. */
  record: boolean;
  /** Whether the list starts shown beside it: opened from the list, or its query in the address. */
  listInitially: boolean;
  /** The pane's `h2`. */
  paneTitle: string;
  /** The list, drawn in the pane — rendered only while it is shown. */
  pane: React.ReactNode;
  /** Where Esc goes: the list, under the search the record was opened from. */
  closeTo: string;
  /** The list page, or the record. */
  children: React.ReactNode;
}) {
  const [shown, setShown] = React.useState(listInitially);
  const navigate = useNavigate();
  const titleId = React.useId();

  React.useEffect(() => {
    if (listInitially) setShown(true);
  }, [listInitially]);
  React.useEffect(() => {
    if (!record) setShown(false);
  }, [record]);

  useWorkspaceWidth('split', record && shown);

  useEscapeClose(() => navigate(closeTo), record);

  const context = React.useMemo(
    () => ({ shown, toggle: () => setShown((current) => !current) }),
    [shown]
  );

  if (!record) return <main className="min-w-0 flex-1">{children}</main>;

  return (
    <div className="flex min-w-0 flex-1">
      {shown && (
        <aside
          aria-labelledby={titleId}
          className="sticky top-12 hidden h-[calc(100dvh-3rem)] w-80 shrink-0 flex-col border-r border-border bg-white lg:flex"
        >
          <h2
            id={titleId}
            className="px-4 pt-3.5 pb-1 text-base leading-tight font-medium text-[var(--color-royal-blue)]"
          >
            {paneTitle}
          </h2>
          {pane}
        </aside>
      )}
      <main className="min-w-0 flex-1">
        <SplitContext.Provider value={context}>{children}</SplitContext.Provider>
      </main>
    </div>
  );
}

/** The record header's switch for its list — drawn by `PageHeader` inside a split record. */
export function SplitToggle() {
  const split = useSplit();
  if (!split) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={split.toggle}
      className="-ml-2 hidden h-7 gap-1.5 px-2 font-normal text-muted-foreground lg:inline-flex"
    >
      {split.shown ? (
        <PanelLeftClose className="size-4" aria-hidden />
      ) : (
        <PanelLeftOpen className="size-4" aria-hidden />
      )}
      {split.shown ? 'Hide list' : 'Show list'}
    </Button>
  );
}

export interface ISplitItem {
  key: string;
  /** The record's address, and the state its link leaves — the list's search. */
  to: string;
  state?: unknown;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  trailing?: React.ReactNode;
}

/**
 * The list as the pane draws it: one line of the record's name, one of what tells it apart, a
 * chip at the right — a link each, the open one `aria-current`. ↑ and ↓ move between them.
 *
 * Its words are the list's own: `status` is what it says in place of rows (loading, empty, past
 * the end, a read that failed), and `footer` its paging.
 */
export function SplitList({
  items,
  selected,
  status,
  footer,
  label
}: {
  items: Nullable<ISplitItem[]>;
  selected: Nullable<string>;
  status?: React.ReactNode;
  footer?: React.ReactNode;
  /** The list's accessible name. */
  label: string;
}) {
  const list = React.useRef<HTMLUListElement>(null);

  const onKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    const links = [...(list.current?.querySelectorAll<HTMLAnchorElement>(':scope > li > a') ?? [])];
    const index = links.indexOf(document.activeElement as HTMLAnchorElement);
    const target = listKeyTarget(event.key, index, links.length - 1);
    if (target === null) return;
    const link = links[Math.max(0, Math.min(links.length - 1, target))];
    if (link) {
      event.preventDefault();
      link.focus();
    }
  };

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {items === null || items.length === 0 ? (
          <div className="px-4 py-3 text-[0.8125rem] text-muted-foreground">
            {/* Not the kit's skeleton: the suite counts those across the page, and the record's
                own loading is what it is asking about. */}
            {status ?? (
              <span aria-hidden className="grid gap-2">
                {Array.from({ length: 8 }, (_, index) => (
                  <span key={index} className="h-9 animate-pulse rounded-md bg-muted" />
                ))}
              </span>
            )}
          </div>
        ) : (
          <ul ref={list} aria-label={label} onKeyDown={onKeyDown} className="grid">
            {items.map((item) => {
              const current = item.key === selected;
              return (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    state={item.state}
                    aria-current={current ? 'true' : undefined}
                    className={cn(
                      'flex items-start gap-3 border-b border-border px-4 py-2 text-[0.8125rem] outline-none',
                      'hover:bg-[var(--accent)] focus-visible:bg-[var(--accent)] focus-visible:shadow-[inset_3px_0_0_var(--color-teal)]',
                      current &&
                        'bg-[color-mix(in_srgb,var(--color-teal)_8%,white)] shadow-[inset_3px_0_0_var(--color-teal)]'
                    )}
                  >
                    <span className="grid min-w-0 flex-1 gap-0.5">
                      <span className="truncate font-medium text-[var(--color-royal-blue)]">
                        {item.primary}
                      </span>
                      {item.secondary && (
                        <span className="line-clamp-2 text-xs text-muted-foreground">
                          {item.secondary}
                        </span>
                      )}
                    </span>
                    {/* The name has the room: the chip takes at most under half the line. */}
                    {item.trailing && (
                      <span className="flex max-w-[45%] min-w-0 shrink-0 justify-end pt-0.5">
                        {item.trailing}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {footer && (
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {footer}
        </div>
      )}
    </>
  );
}

/**
 * The pane's paging: where it is, and the way on and back. Worded apart from the list page's
 * pager — "Page 2 of 7 · 312 found" — because both can be on one screen, the record's own lists
 * below, and a spec reads a pager's count by its words.
 */
export function SplitPager({
  page,
  pageSize,
  total,
  onPage
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <>
      <span className="tabular-nums">
        Page {page} of {lastPage} · {total} found
      </span>
      <span className="flex gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label="Previous page of the list"
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          aria-label="Next page of the list"
        >
          Next
        </Button>
      </span>
    </>
  );
}

/** Where a key moves focus in the pane's list, before clamping — or null for a key it ignores. */
function listKeyTarget(key: string, index: number, last: number): Nullable<number> {
  switch (key) {
    case 'Home':
      return 0;
    case 'End':
      return last;
    case 'ArrowDown':
      return index + 1;
    case 'ArrowUp':
      return index - 1;
    default:
      return null;
  }
}

/**
 * Whether an Esc is free to close a record — the split view's rule, and a list's reading pane's
 * (SR-230): never a repeat, never out of a field, never while a menu, listbox, popover or dialog is
 * open (whose Esc it is), and never while the page holds something no re-read brings back
 * (`SPLIT_HOLD`). Asked from a captured listener, so an overlay closing on this same press is still
 * in the page.
 */
export function escapeIsFree(event: KeyboardEvent): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented || event.repeat) return false;
  return !isField(event.target) && !overlayOpen() && !held();
}

/**
 * Closes a record on a free Esc (`escapeIsFree`) — the split view's, a list's reading pane's and DD
 * calls' panel's one listener. Captured, so a menu or a dialog closing on this same press is still
 * in the page when the question is asked; the latest `close` is called, never the first render's.
 */
export function useEscapeClose(close: () => void, enabled = true): void {
  const closeRef = React.useRef(close);
  closeRef.current = close;
  React.useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (!escapeIsFree(event)) return;
      event.preventDefault();
      closeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled]);
}

/** A record holding itself open, over something closing it would destroy (`SPLIT_HOLD`). */
function held(): boolean {
  return document.querySelector('[data-split-hold]') !== null;
}

/** A menu, a listbox, a popover or a dialog open anywhere — whose Esc it is. */
function overlayOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]'
    ) !== null
  );
}
